/**
 * PendingApprovalsContext — v27.3
 * Exposes a role-scoped pending-expense count that the sidebar (and any other
 * consumer) can render as a badge. Polls once per 60s and re-fetches on demand
 * after every expense mutation (create / approve / send-back / reject / pay).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PendingApprovalsContext = createContext(null);

const POLL_MS = 60 * 1000; // 60s — cheap MongoDB count() query

export function PendingApprovalsProvider({ children }) {
  const { user } = useAuth();
  const [expensesPending, setExpensesPending] = useState(0);
  const [stage, setStage] = useState(null);
  const timerRef = useRef(null);

  const refreshExpensesPending = useCallback(async () => {
    // Skip while auth is loading (null) or logged out (false)
    if (!user || !user.role) return;
    try {
      const { data } = await api.get("/expenses/pending-count");
      setExpensesPending(Number(data?.count || 0));
      setStage(data?.stage || null);
    } catch { /* silent — badge just stays stale */ }
  }, [user]);

  // Poll timer — starts on login, cleared on logout / unmount
  useEffect(() => {
    if (!user || !user.role) {
      setExpensesPending(0);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    refreshExpensesPending();
    timerRef.current = setInterval(refreshExpensesPending, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refreshExpensesPending]);

  const value = useMemo(
    () => ({ expensesPending, stage, refreshExpensesPending }),
    [expensesPending, stage, refreshExpensesPending]
  );

  return (
    <PendingApprovalsContext.Provider value={value}>
      {children}
    </PendingApprovalsContext.Provider>
  );
}

export const usePendingApprovals = () => {
  const ctx = useContext(PendingApprovalsContext);
  // Safe fallback when consumed outside the provider (e.g. login page).
  return ctx || { expensesPending: 0, stage: null, refreshExpensesPending: () => {} };
};
